import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import type { AxiosError } from 'axios';

import PageContainer from '@/components/Container/PageContainer';
import DataTable from '@/components/DataTable';
import { Button } from '@/components/UI/Button';
import Loading from '@/components/UI/Loading';
import { convertToFileSize } from '@/utils/convert';
import { errorHandler, successHandler } from '@/utils/toastHandler';
import {
  blockchainUploadMessageKey,
  createBlockchainIdempotencyKey,
} from '@/utils/blockchainDocuments';
import {
  archiveBlockchainDocument,
  downloadBlockchainDocument,
  getBlockchainDocuments,
  retryBlockchainDocument,
  unarchiveBlockchainDocument,
  uploadBlockchainDocument,
} from '@/api/blockchainDocuments.api';
import type { BlockchainDocument, BlockchainDocumentListResponse, BlockchainDocumentStatus } from '@/types/blockchainDocuments';
import {
  ActionGroup,
  BrowseLabel,
  Content,
  EmptyCopy,
  EmptyState,
  EmptyTitle,
  FilePicker,
  HiddenFileInput,
  IntegrityBadge,
  Notice,
  Panel,
  SectionHeader,
  SectionHeading,
  SelectedFileName,
  Stat,
  StatGrid,
  StatsPanel,
  StatusBadge,
  TableArea,
  Toolbar,
  ToolbarActions,
  UploadCopy,
  UploadLabel,
  UploadRow,
} from './style';

import { useBlockchainPolling } from './useBlockchainPolling';

type UploadForm = { file: FileList };
type SourceFilter = 'all' | 'manual' | 'deal';
type StatusFilter = 'all' | BlockchainDocumentStatus;

type ErrorEnvelope = { message?: string };
const isAxiosError = (error: unknown): error is AxiosError<ErrorEnvelope> =>
  typeof error === 'object' &&
  error !== null &&
  (error as { isAxiosError?: boolean }).isAxiosError === true;

const apiMessage = (error: unknown, fallback: string) =>
  isAxiosError(error) ? error.response?.data?.message || fallback : fallback;

const BlockchainDocuments = () => {
  // @ts-ignore deeply nested legacy locale types exceed CRA's compiler limit.
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState<BlockchainDocument[]>([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [source, setSource] = useState<SourceFilter>('all');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [archived, setArchived] = useState<0 | 1>(0);
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [storageStatus, setStorageStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [submitting, setSubmitting] = useState(false);
  const [submissionEnabled, setSubmissionEnabled] = useState(false);
  const [storage, setStorage] = useState<BlockchainDocumentListResponse['storage'] | null>(null);
  const loadVersion = useRef(0);
  const uploadKey = useRef<string | null>(null);
  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm<UploadForm>();
  const selectedFile = watch('file')?.[0];
  const hasActiveFilters = source !== 'all' || status !== 'all' || archived === 1 || query !== '';

  const load = useCallback(async (silent = false) => {
    const version = ++loadVersion.current;
    if (!silent) {
      setLoading(true);
      setStorageStatus('loading');
    }
    try {
      const response = await getBlockchainDocuments({ source, status, archived, q: query || undefined, page, per_page: perPage });
      if (version !== loadVersion.current) return;
      setItems(response.data.data.items);
      setTotal(response.data.data.pagination.total);
      setSubmissionEnabled(response.data.data.submission_enabled);
      setStorage(response.data.data.storage);
      setStorageStatus('ready');
    } catch (error: unknown) {
      if (version === loadVersion.current) {
        setStorageStatus('error');
        if (!silent) errorHandler(apiMessage(error, t('blockchainDocuments.errors.load')));
      }
    } finally {
      if (version === loadVersion.current) setLoading(false);
    }
  }, [archived, page, perPage, query, source, status, t]);

  useEffect(() => { load(); return () => { loadVersion.current += 1; }; }, [load]);

  const hasProcessing = items.some((item) => {
    const anchorStatus = item.anchor_status || item.status;
    return anchorStatus === 'queued' || anchorStatus === 'submitting'
      || (anchorStatus === 'result_unknown' && item.failure_category !== 'lookup_object_mismatch');
  });
  const polling = useBlockchainPolling(hasProcessing, () => load(true));

  const submitUpload = handleSubmit(async ({ file }) => {
    const selected = file?.[0];
    if (!selected || submitting) return;
    if (selected.size > 100 * 1024 * 1024) { errorHandler(t('blockchainDocuments.errors.fileSize')); return; }
    setSubmitting(true);
    uploadKey.current = uploadKey.current || createBlockchainIdempotencyKey();
    try {
      const response = await uploadBlockchainDocument(selected, uploadKey.current);
      const { document, reused } = response.data.data;
      successHandler(t(`blockchainDocuments.messages.${blockchainUploadMessageKey(document, reused)}`));
      uploadKey.current = null;
      reset();
      polling.restart();
      await load(true);
    } catch (error: unknown) {
      if (isAxiosError(error) && error.response?.status === 422) uploadKey.current = null;
      errorHandler(apiMessage(error, t('blockchainDocuments.errors.upload')));
    } finally { setSubmitting(false); }
  });

  const download = async (document: BlockchainDocument) => {
    try {
      const response = await downloadBlockchainDocument(document.id);
      const url = URL.createObjectURL(response.data);
      const anchor = window.document.createElement('a');
      anchor.href = url;
      anchor.download = document.file_name;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error: unknown) { errorHandler(apiMessage(error, t('blockchainDocuments.errors.download'))); }
  };

  const mutate = async (document: BlockchainDocument, action: 'archive' | 'unarchive' | 'retry') => {
    try {
      if (action === 'archive') await archiveBlockchainDocument(document.id);
      if (action === 'unarchive') await unarchiveBlockchainDocument(document.id);
      if (action === 'retry') await retryBlockchainDocument(document.id);
      successHandler(t(`blockchainDocuments.messages.${action}`));
      polling.restart();
      await load(true);
    } catch (error: unknown) { errorHandler(apiMessage(error, t('blockchainDocuments.errors.action'))); }
  };

  const clearFilters = () => {
    setSource('all');
    setStatus('all');
    setArchived(0);
    setSearchInput('');
    setQuery('');
    setPage(1);
  };

  const columns = useMemo<ColumnDef<BlockchainDocument>[]>(() => [
    { accessorKey: 'file_name', header: t('blockchainDocuments.columns.file'), meta: { mobileLabel: t('blockchainDocuments.columns.file') } },
    { accessorKey: 'source', header: t('blockchainDocuments.columns.source'), cell: ({ row }) => t(`blockchainDocuments.sources.${row.original.source}`), meta: { mobileLabel: t('blockchainDocuments.columns.source') } },
    { accessorKey: 'file_size', header: t('blockchainDocuments.columns.size'), cell: ({ row }) => convertToFileSize(row.original.file_size), meta: { mobileLabel: t('blockchainDocuments.columns.size') } },
    { accessorKey: 'integrity_status', header: t('blockchainDocuments.columns.integrity'), cell: () => <IntegrityBadge>{t('blockchainDocuments.integrity.hash_available')}</IntegrityBadge>, meta: { mobileLabel: t('blockchainDocuments.columns.integrity') } },
    { accessorKey: 'anchor_status', header: t('blockchainDocuments.columns.blockchainStatus'), cell: ({ row }) => {
      const anchorStatus = row.original.anchor_status || row.original.status;
      return <StatusBadge $status={anchorStatus}>{t(`blockchainDocuments.statuses.${anchorStatus}`)}</StatusBadge>;
    }, meta: { mobileLabel: t('blockchainDocuments.columns.blockchainStatus') } },
    { accessorKey: 'created_at', header: t('blockchainDocuments.columns.created'), cell: ({ row }) => row.original.created_at ? new Intl.DateTimeFormat(i18n.language === 'sc' ? 'zh-CN' : i18n.language === 'tc' ? 'zh-TW' : 'en', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(row.original.created_at)) : '-', meta: { mobileLabel: t('blockchainDocuments.columns.created') } },
    {
      id: 'actions', header: t('blockchainDocuments.columns.actions'), meta: { mobileLabel: t('blockchainDocuments.columns.actions') },
      cell: ({ row }) => {
        const document = row.original;
        const available = document.available_actions;
        return <ActionGroup>
          {available.includes('download') && <button type="button" onClick={() => download(document)}>{t('buttons.download')}</button>}
          {available.includes('retry') && <button type="button" onClick={() => mutate(document, 'retry')}>{t('blockchainDocuments.actions.retry')}</button>}
          {available.includes('archive') && <button type="button" onClick={() => mutate(document, 'archive')}>{t('blockchainDocuments.actions.archive')}</button>}
          {available.includes('unarchive') && <button type="button" onClick={() => mutate(document, 'unarchive')}>{t('blockchainDocuments.actions.unarchive')}</button>}
          {available.includes('go_to_deal') && document.deal && <button type="button" onClick={() => navigate(`/deal/${document.deal?.id}`)}>{t('blockchainDocuments.actions.goToDeal')}</button>}
        </ActionGroup>;
      },
    },
  ], [i18n.language, load, navigate, t]);

  return <PageContainer title={t('blockchainDocuments.title')}>
    <Content>
      <Panel>
        <SectionHeader>
          <SectionHeading>{t('blockchainDocuments.uploadTitle')}</SectionHeading>
        </SectionHeader>
        <Notice>{t('blockchainDocuments.hashNotice')}</Notice>
        {!submissionEnabled && <p role="status">{t('blockchainDocuments.submissionDisabled')}</p>}
        <UploadRow onSubmit={submitUpload}>
          <FilePicker $disabled={submitting}>
            <UploadCopy>
              <UploadLabel>{t('blockchainDocuments.chooseFile')}</UploadLabel>
              <SelectedFileName aria-live="polite">
                {selectedFile?.name || t('blockchainDocuments.noFileSelected')}
              </SelectedFileName>
            </UploadCopy>
            <BrowseLabel $disabled={submitting}>
              {t('blockchainDocuments.browse')}
              <HiddenFileInput
                type="file"
                aria-label={t('blockchainDocuments.chooseFile')}
                disabled={submitting}
                {...register('file', { required: t('blockchainDocuments.errors.fileRequired') })}
              />
            </BrowseLabel>
          </FilePicker>
          <Button type="submit" variant="primary" disabled={submitting}>{submitting ? t('blockchainDocuments.uploading') : t('buttons.upload')}</Button>
          {errors.file && <p className="error" role="alert">{errors.file.message}</p>}
        </UploadRow>
      </Panel>

      <StatsPanel aria-label={t('blockchainDocuments.stats.label')} aria-busy={storageStatus === 'loading'}>
        {storageStatus === 'loading' ? (
          <p role="status">{t('loading')}</p>
        ) : storageStatus === 'error' ? (
          <div>
            <p role="alert">{t('blockchainDocuments.errors.load')}</p>
            <Button type="button" variant="primary-invert" onClick={() => void load()}>
              {t('blockchainDocuments.actions.refresh')}
            </Button>
          </div>
        ) : storage && (
          <StatGrid>
            <Stat><span>{t('blockchainDocuments.stats.total')}</span><strong>{convertToFileSize(storage.blockchain_size)}</strong></Stat>
            <Stat><span>{t('blockchainDocuments.stats.confirmed')}</span><strong>{convertToFileSize(storage.blockchain_confirmed_size)}</strong></Stat>
            <Stat><span>{t('blockchainDocuments.stats.processing')}</span><strong>{convertToFileSize(storage.processing_size)}</strong></Stat>
            <Stat><span>{t('blockchainDocuments.stats.failed')}</span><strong>{convertToFileSize(storage.failed_unknown_size)}</strong></Stat>
          </StatGrid>
        )}
      </StatsPanel>

      <Panel>
        <SectionHeader>
          <SectionHeading>{t('blockchainDocuments.documentsTitle')}</SectionHeading>
        </SectionHeader>
        <Toolbar onSubmit={(event) => { event.preventDefault(); setQuery(searchInput.trim()); setPage(1); }}>
          <label htmlFor="blockchain-source">{t('blockchainDocuments.filters.source')}<select id="blockchain-source" name="blockchain-source" value={source} onChange={(e) => { setSource(e.target.value as SourceFilter); setPage(1); }}><option value="all">{t('blockchainDocuments.filters.all')}</option><option value="manual">{t('blockchainDocuments.sources.manual')}</option><option value="deal">{t('blockchainDocuments.sources.deal')}</option></select></label>
          <label htmlFor="blockchain-status">{t('blockchainDocuments.filters.status')}<select id="blockchain-status" name="blockchain-status" value={status} onChange={(e) => { setStatus(e.target.value as StatusFilter); setPage(1); }}><option value="all">{t('blockchainDocuments.filters.all')}</option>{(['queued','submitting','confirmed','failed','result_unknown','historical_unverified','disabled'] as BlockchainDocumentStatus[]).map((value) => <option key={value} value={value}>{t(`blockchainDocuments.statuses.${value}`)}</option>)}</select></label>
          <label htmlFor="blockchain-view">{t('blockchainDocuments.filters.view')}<select id="blockchain-view" name="blockchain-view" value={archived} onChange={(e) => { setArchived(Number(e.target.value) as 0 | 1); setPage(1); }}><option value={0}>{t('blockchainDocuments.filters.active')}</option><option value={1}>{t('blockchainDocuments.filters.archived')}</option></select></label>
          <label className="search" htmlFor="blockchain-search">{t('blockchainDocuments.filters.search')}<input id="blockchain-search" name="blockchain-search" autoComplete="off" placeholder={t('blockchainDocuments.filters.searchPlaceholder')} value={searchInput} onChange={(e) => setSearchInput(e.target.value)} /></label>
          <ToolbarActions>
            <Button type="submit" variant="primary">{t('buttons.search')}</Button>
            <Button type="button" variant="primary-invert" onClick={() => { polling.restart(); load(); }}>{t('blockchainDocuments.actions.refresh')}</Button>
          </ToolbarActions>
        </Toolbar>
        {polling.paused && <p role="status">{t('blockchainDocuments.refreshPaused')}</p>}
        <TableArea>
          {loading ? <Loading /> : items.length > 0 ? (
            <DataTable columns={columns} data={items} page={page} perPage={perPage} total={total} setPage={setPage} setPerPage={setPerPage} mobileCardLayout />
          ) : (
            <EmptyState>
              <EmptyTitle>{t(hasActiveFilters ? 'blockchainDocuments.empty.filteredTitle' : 'blockchainDocuments.empty.title')}</EmptyTitle>
              <EmptyCopy>{t(hasActiveFilters ? 'blockchainDocuments.empty.filteredDescription' : 'blockchainDocuments.empty.description')}</EmptyCopy>
              {hasActiveFilters && <Button type="button" variant="primary-invert" onClick={clearFilters}>{t('blockchainDocuments.actions.clearFilters')}</Button>}
            </EmptyState>
          )}
        </TableArea>
      </Panel>
    </Content>
  </PageContainer>;
};

export default BlockchainDocuments;
